import { forwardRef } from "react"
import ReactSelect, {
  ControlProps,
  InputProps,
  MenuListProps,
  MenuProps,
  OptionProps,
  PlaceholderProps,
  SingleValueProps,
  ValueContainerProps,
  DropdownIndicatorProps,
  NoticeProps,
  components,
  ClearIndicatorProps,
} from "react-select"

import {
  StyledOption,
  StyledControl,
  StyledMenu,
  StyledMenuList,
  StyledPlaceholder,
  StyledSingleValue,
  StyledValueContainer,
  StyledInput,
  StyledDropdownIndicator,
  StyledClearIndicator,
  StyledOptionLabel,
} from "./styles"
import { ReactComponent as ArrowBottomIcon } from "assets/icons/arrow-down.svg"
import { ReactComponent as CheckIcon } from "assets/icons/check.svg"
import { ReactComponent as CloseIcon } from "assets/icons/close.svg"
import { Avatar } from "@atoms"

function Option<TData>(props: OptionProps<TData>) {
  const avatar = (props.data as any).avatar
  return (
    <StyledOption {...props}>
      <StyledOptionLabel className="caption">
        {avatar && <Avatar src={avatar} size="small" />}
        <span data-cy={`select_option_${(props as any).value}`}>
          {props.children}
        </span>
      </StyledOptionLabel>
      {props.isSelected && (
        <span>
          <CheckIcon fill="var(--color-primary)" />
        </span>
      )}
    </StyledOption>
  )
}

function Menu<TData>(props: MenuProps<TData>) {
  return <StyledMenu {...props} />
}
function MenuList<TData>(props: MenuListProps<TData>) {
  return <StyledMenuList {...props} />
}
function SingleValue<TData>(props: SingleValueProps<TData>) {
  return (
    <StyledSingleValue {...props}>
      <span className="caption">{props.children}</span>
    </StyledSingleValue>
  )
}
function Placeholder<TData>(props: PlaceholderProps<TData>) {
  return (
    <StyledPlaceholder className="caption" {...props}>
      {props.children}
    </StyledPlaceholder>
  )
}
function Control<TData>(props: ControlProps<TData>) {
  return <StyledControl {...props} />
}
function ValueContainer<TData>(props: ValueContainerProps<TData>) {
  return <StyledValueContainer {...props} />
}
function Input<TData>(props: InputProps<TData>) {
  return (
    <StyledInput {...props} className={`caption ${props.className ?? ""}`} />
  )
}
function DropdownIndicator<TData>(props: DropdownIndicatorProps<TData>) {
  return (
    <StyledDropdownIndicator {...props}>
      <ArrowBottomIcon className="small" fill="var(--color-neutral-200)" />
    </StyledDropdownIndicator>
  )
}
function IndicatorSeparator() {
  return null
}
function NoOptionsMessage<TData>(props: NoticeProps<TData>) {
  return (
    <components.NoOptionsMessage
      {...props}
      className={`caption ${props.className ?? ""}`}
    />
  )
}
function LoadingMessage<TData>(props: NoticeProps<TData>) {
  return (
    <components.LoadingMessage
      {...props}
      className={`caption ${props.className ?? ""}`}
    />
  )
}

function ClearIndicator<TData>(props: ClearIndicatorProps<TData>) {
  return (
    <StyledClearIndicator {...props}>
      <CloseIcon width="8" height="8" fill="var(--color-neutral-200)" />
    </StyledClearIndicator>
  )
}
interface Props extends React.ComponentPropsWithRef<typeof ReactSelect> {
  variant?: "default" | "contained"
  state?: "error" | "warning"
  size?: "default" | "small"
}
const Select: React.FC<Props> = forwardRef((props, ref) => {
  return (
    <ReactSelect
      ref={ref}
      {...props}
      menuPosition={props.menuPosition || "fixed"}
      menuPortalTarget={
        props.menuPortalTarget ||
        (typeof document !== "undefined" ? document.body : undefined)
      }
      components={{
        Option,
        Menu,
        MenuList,
        SingleValue,
        Placeholder,
        Control,
        ValueContainer,
        Input,
        IndicatorSeparator,
        DropdownIndicator,
        NoOptionsMessage,
        ClearIndicator,
        LoadingMessage,
        ...props.components,
      }}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 9999999999 }),
        multiValue: (provided) => ({
          ...provided,
          backgroundColor: "var(--color-neutral-50)",
          borderRadius: 99999,
        }),
        multiValueRemove: (base) => ({
          ...base,
          borderRadius: 9999,
          backgroundColor: "var(--color-neutral-50)",
          transition: "background-color var(--duration-150) linear",
          color: "var(--color-neutral-300)",
          "&:hover": {
            color: "var(--color-neutral-400)",
            backgroundColor: "var(--color-neutral-75)",
          },
        }),
      }}
    />
  )
})
Select.displayName = "Select"
Select.defaultProps = { variant: "default" }
export default Select
